import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "../../../components/ui/table";
import { Button } from "../../../components/ui/button";
import { 
  ArrowLeft, 
  Download, 
  FileText, 
  Trash2, 
  Search,
  AlertCircle,
  CheckCircle,
  X
} from "lucide-react";
import { Input } from "../../../components/ui/input";
import { Badge } from "../../../components/ui/badge";
import { Card } from "../../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { useToast } from "../../../components/ui/use-toast";
import { importApi } from "../../../api/importApi";
import { customerApi } from "../../../api/customerApi";

const ImportHistoryPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [importHistory, setImportHistory] = useState([]);
  const [importToDelete, setImportToDelete] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteDialogMode, setDeleteDialogMode] = useState<'none' | 'exclude_learners' | 'all'>('none');
  const [customersWithSessions, setCustomersWithSessions] = useState<any[]>([]);
  const [loadingCustomersWithSessions, setLoadingCustomersWithSessions] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const fetchImports = useCallback(async () => {
    try {
      setIsLoading(true);
      const accessToken = localStorage.getItem('accessToken');
      const data = await importApi.getImports(accessToken);
      setImportHistory(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching imports:", error);
      toast({
        title: "Erreur",
        description: "Impossible de charger l'historique des imports",
        variant: "destructive",
      });
      setImportHistory([]);
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchImports();
  }, [fetchImports]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const filteredHistory = Array.isArray(importHistory) 
    ? importHistory.filter(item => item && item.name && item.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : [];

  const handleDeleteImport = async (id: number) => {
    setImportToDelete(id);
    setDeleteDialogMode('none');
    setIsDeleteDialogOpen(true);
    try {
      setLoadingCustomersWithSessions(true);
      const accessToken = localStorage.getItem('accessToken');
      const data = await importApi.getCustomersWithSessionsByImport(accessToken, id);
      setCustomersWithSessions(Array.isArray(data) ? data : []);
    } catch (e) {
      setCustomersWithSessions([]);
    } finally {
      setLoadingCustomersWithSessions(false);
    }
  };

  const confirmDelete = async () => {
    if (importToDelete) {
      try {
        const accessToken = localStorage.getItem('accessToken');
        if (deleteDialogMode === 'none') {
          setIsDeleteDialogOpen(false);
          setImportToDelete(null);
          return;
        }
        const result = await importApi.deleteCustomersByImport(accessToken, importToDelete, deleteDialogMode);
        if (result.status === 200) {
          toast({
            title: 'Suppression',
            description: 'La suppression a été effectuée',
          });
          fetchImports();
        } else {
          throw new Error(result.message || 'Erreur lors de la suppression');
        }
      } catch (error) {
        console.error("Error deleting import:", error);
        toast({
          title: "Erreur",
          description: "Impossible d'exécuter l'action",
          variant: "destructive",
        });
      } finally {
        setIsDeleteDialogOpen(false);
        setImportToDelete(null);
        setCustomersWithSessions([]);
      }
    }
  };

  const handleDownload = async (id: number, name: string) => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      // Utiliser l'export CSV standard, mais filtré par import_id côté backend, pour avoir
      // exactement les mêmes colonnes que l'export “Liste des apprenants”
      const resp = await customerApi.exportCustomersByImport(accessToken, id);

      if (resp.status === 200) {
        const blob = new Blob([resp.data], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const safeName = (name || `import_${id}`)
          .toString()
          // Remove invalid filename characters and normalize accents
          .normalize('NFKD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[\\/:*?"<>|]+/g, '')
          .replace(/\s+/g, '_')
          .slice(0, 100);
        const filename = `export_${safeName}_${new Date().toISOString().split('T')[0]}.csv`;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast({
          title: "Téléchargement",
          description: "Le fichier a été téléchargé avec succès",
        });
      } else {
        throw new Error(resp.error || 'Erreur lors du téléchargement');
      }
    } catch (error) {
      console.error("Error downloading import:", error);
      toast({
        title: "Erreur",
        description: "Impossible de télécharger le fichier",
        variant: "destructive",
      });
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="container py-8">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate("/dashboard/new-imports")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-bold">Historique des imports</h1>
        </div>
        
        <Button onClick={() => navigate("/dashboard/csv-import")}>
          <FileText className="h-4 w-4 mr-2" />
          Nouvel import
        </Button>
      </div>
      
      <Card className="bg-white p-6">
        <div className="mb-6">
          <div className="relative w-full max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Rechercher un import..."
              value={searchQuery}
              onChange={handleSearch}
              className="pl-10"
            />
          </div>
        </div>
        
        {isLoading ? (
          <div className="text-center py-10">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement de l'historique...</p>
          </div>
        ) : filteredHistory.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Date d&apos;import</TableHead>
                  <TableHead>Contacts</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Importé par</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredHistory.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell>{formatDate(item.created_at)}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-medium">{item.total_records} contacts</span>
                        <div className="flex items-center text-xs text-gray-500 gap-2">
                          <span className="flex items-center">
                            <CheckCircle className="h-3 w-3 text-green-500 mr-1" />
                            {item.success_count} importés
                          </span>
                          {item.duplicate_count > 0 && (
                            <span className="flex items-center">
                              <AlertCircle className="h-3 w-3 text-amber-500 mr-1" />
                              {item.duplicate_count} doublons
                            </span>
                          )}
                          {item.failure_count > 0 && (
                            <span className="flex items-center">
                              <X className="h-3 w-3 text-red-500 mr-1" />
                              {item.failure_count} échecs
                            </span>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant="outline"
                        className={`${
                          item.status === 'completed' 
                            ? "bg-green-50 text-green-700 border-green-200"
                            : item.status === 'failed'
                              ? "bg-red-50 text-red-700 border-red-200"
                              : item.status === 'processing'
                                ? "bg-blue-50 text-blue-700 border-blue-200"
                                : "bg-gray-50 text-gray-700 border-gray-200"
                        }`}
                      >
                        {item.status === 'completed' 
                          ? "Terminé" 
                          : item.status === 'failed'
                            ? "Échoué"
                            : item.status === 'processing'
                              ? "En cours"
                              : item.status === 'pending'
                                ? "En attente"
                                : item.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{item.user_name}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDownload(item.id, item.name)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDeleteImport(item.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-10">
            <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun import trouvé</h3>
            <p className="text-gray-500">
              {searchQuery
                ? `Aucun résultat pour "${searchQuery}"`
                : "Vous n'avez pas encore importé de contacts"}
            </p>
          </div>
        )}
      </Card>
      
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogDescription>
              Voici les apprenants liés à cet import.
            </DialogDescription>
          </DialogHeader>
          {loadingCustomersWithSessions ? (
            <div className="text-sm text-gray-600">Chargement des apprenants avec cours…</div>
          ) : customersWithSessions.length > 0 ? (
            <div className="max-h-60 overflow-auto border rounded p-2 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left">
                    <th className="py-1 pr-2">Nom</th>
                    <th className="py-1 pr-2">Prénom</th>
                    <th className="py-1 pr-2">Email</th>
                    <th className="py-1 pr-2">Téléphone</th>
                    <th className="py-1 pr-2">Profession</th>
                  </tr>
                </thead>
                <tbody>
                  {customersWithSessions.map((c) => (
                    <tr key={c.id} className="border-t">
                      <td className="py-1 pr-2">{c.lastname}</td>
                      <td className="py-1 pr-2">{c.firstname}</td>
                      <td className="py-1 pr-2">{c.email}</td>
                      <td className="py-1 pr-2">{c.phone}</td>
                      <td className="py-1 pr-2">{c.profession}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-sm text-gray-600 mb-4">Aucun apprenant avec cours trouvé pour cet import.</div>
          )}
          <div className="flex flex-col gap-2 mb-2">
            <Button variant={deleteDialogMode === 'none' ? 'default' : 'outline'} onClick={() => setDeleteDialogMode('none')}>Annuler la suppression</Button>
            {customersWithSessions.length > 0 && (
              <Button variant={deleteDialogMode === 'exclude_learners' ? 'default' : 'outline'} onClick={() => setDeleteDialogMode('exclude_learners')}>Supprimer sauf les apprenants</Button>
            )}
            <Button variant={deleteDialogMode === 'all' ? 'destructive' : 'outline'} onClick={() => setDeleteDialogMode('all')}>Tout supprimer</Button>
          </div>
          <DialogFooter>
            <Button variant="default" onClick={confirmDelete}>
              <CheckCircle className="h-4 w-4 mr-2" />
              Valider l'action
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImportHistoryPage;
